// From: https://github.com/backstage/backstage/blob/master/plugins/home/src/homePageComponents/WelcomeTitle/timeUtil.ts

/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// Cannot comment origin for locale files in JSON files
// https://github.com/backstage/backstage/blob/master/plugins/home/src/homePageComponents/WelcomeTitle/locales/goodMorning.locales.json
// https://github.com/backstage/backstage/blob/master/plugins/home/src/homePageComponents/WelcomeTitle/locales/goodAfternoon.locales.json
// https://github.com/backstage/backstage/blob/master/plugins/home/src/homePageComponents/WelcomeTitle/locales/goodEvening.locales.json

import goodAfternoon from '../locales/goodAfternoon.locales.json';
import goodEvening from '../locales/goodEvening.locales.json';
import goodMorning from '../locales/goodMorning.locales.json';

enum Times {
  MORNING = 'Good Morning',
  AFTERNOON = 'Good Afternoon',
  EVENING = 'Good Evening',
}

// Seed is set to current date, so that greetings change everyday
// and same for all users.
const todaysDate = new Date().toLocaleDateString('en-IN').replaceAll('/', '');
const greetingRandomSeed = parseInt(todaysDate, 10);

export function getTimeBasedGreeting(): { tooltip: string; greeting: string } {
  const currentHour = new Date(Date.now()).getHours();

  if (currentHour >= 23) {
    return {
      tooltip: 'Seriously',
      greeting: 'Get some rest',
    };
  }

  const getCurrentTime = (): Times => {
    if (currentHour < 12) return Times.MORNING;
    if (currentHour < 17) return Times.AFTERNOON;
    return Times.EVENING;
  };

  const getLocaleSetFrom = (time: Times): { [language: string]: string } => {
    switch (time) {
      case Times.MORNING:
        return goodMorning;
      case Times.AFTERNOON:
        return goodAfternoon;
      default:
        return goodEvening;
    }
  };

  const selectRandomLocaleFrom = (array: string[]) =>
    array[greetingRandomSeed % array.length];

  const currentTime = getCurrentTime();
  const greetings = getLocaleSetFrom(currentTime);
  const selectedLanguage = selectRandomLocaleFrom(Object.keys(greetings));
  const selectedGreeting = greetings[selectedLanguage];

  const tooltipLanguage =
    selectedLanguage?.length > 0 ? selectedLanguage : 'English';

  return {
    tooltip: `${currentTime.toString()} (${tooltipLanguage})`,
    greeting: selectedGreeting?.length > 0 ? selectedGreeting : 'Welcome',
  };
}
